import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * Idle-timeout enforcement for authenticated sessions.
 *
 * Tracks last user activity in localStorage so the timer is shared across all
 * open tabs of the app: activity in one tab keeps every tab alive.
 *
 * Flow:
 *  - Standard activity events (mousemove, keydown, click, scroll, touchstart)
 *    update the shared lastActivity timestamp (throttled to once per 10s).
 *  - After IDLE_MS (30 min) of no activity, the user is signed out and sent to
 *    /login?reason=idle-timeout.
 *  - WARN_MS before the timeout (default 60s), a warning dialog is surfaced
 *    offering "Stay signed in" (which simply refreshes lastActivity).
 *
 * The hook only runs when a session exists (the caller mounts it inside
 * ProtectedRoute, which already gates on session presence).
 */

const IDLE_MS = 30 * 60 * 1000; // 30 minutes
const WARN_MS = 60 * 1000; // 1 minute warning before sign-out
const STORAGE_KEY = "emr:lastActivity";
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "keydown",
  "click",
  "scroll",
  "touchstart",
];

/** Read the shared last-activity timestamp (ms since epoch). */
function readLastActivity(): number {
  const raw = localStorage.getItem(STORAGE_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : Date.now();
}

/** Write the shared last-activity timestamp. */
function writeLastActivity(ts: number): void {
  localStorage.setItem(STORAGE_KEY, String(ts));
}

export interface IdleTimeoutState {
  /** True when the warning dialog should be shown. */
  warning: boolean;
  /** Seconds remaining before sign-out when warning is active. */
  secondsRemaining: number;
  /** Dismiss the warning by refreshing activity (user clicked "Stay signed in"). */
  staySignedIn: () => void;
}

/**
 * Enforces idle-timeout for the current authenticated session.
 * Returns state for the warning dialog; the actual sign-out happens here.
 */
export function useIdleTimeout(): IdleTimeoutState {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();

  const [warning, setWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.ceil(WARN_MS / 1000));

  // Throttle bookkeeping so we don't write to localStorage on every mousemove.
  const lastWriteRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);

  /** Refresh the shared activity timestamp (throttled). */
  const bumpActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastWriteRef.current < 10_000) return; // at most once / 10s
    lastWriteRef.current = now;
    writeLastActivity(now);
    // Dismiss an active warning the moment the user does anything.
    setWarning(false);
  }, []);

  /** Hard sign-out path on idle expiry. */
  const signOutIdle = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEY);
    setWarning(false);
    await signOut();
    navigate("/login?reason=idle-timeout", { replace: true });
  }, [signOut, navigate]);

  // 1) Attach activity listeners for the whole document.
  useEffect(() => {
    if (!session) return; // only when authenticated
    // Seed the timestamp on mount so a stale value from a previous session
    // doesn't immediately trigger a timeout.
    writeLastActivity(Date.now());
    lastWriteRef.current = Date.now();

    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, bumpActivity, { passive: true });
    }
    // Cross-tab sync: if another tab bumps activity, this tab sees it.
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        lastWriteRef.current = Number(e.newValue);
        setWarning(false);
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, bumpActivity);
      }
      window.removeEventListener("storage", onStorage);
    };
  }, [session, bumpActivity]);

  // 2) Polling tick: every 1s, check idle elapsed and drive warning / sign-out.
  useEffect(() => {
    if (!session) return;

    const tick = () => {
      const idleFor = Date.now() - readLastActivity();
      if (idleFor >= IDLE_MS) {
        // Fully idle → sign out.
        signOutIdle();
        return;
      }
      if (idleFor >= IDLE_MS - WARN_MS) {
        // Warning window.
        const remainingMs = IDLE_MS - idleFor;
        setSecondsRemaining(Math.max(1, Math.ceil(remainingMs / 1000)));
        setWarning(true);
      } else {
        // Still active enough.
        setWarning(false);
      }
    };

    // Run immediately then on a 1s interval. setInterval returns a number in
    // the browser; we keep a ref so we can clean up deterministically.
    tick();
    tickRef.current = window.setInterval(tick, 1000);
    return () => {
      if (tickRef.current != null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [session, signOutIdle]);

  const staySignedIn = useCallback(() => {
    const now = Date.now();
    writeLastActivity(now);
    lastWriteRef.current = now;
    setWarning(false);
  }, []);

  return { warning, secondsRemaining, staySignedIn };
}
